import type { File } from "@smartsuite/types";
import fastdom from "fastdom";
import type { TransformOptions } from "filestack-js";
import type React from "react";
import {
  type FunctionComponent,
  memo,
  type RefObject,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";

import { useFileStackContext } from "../../../hooks/useFileStackContext";

import "./FilestackImage.sass";

const loadingClassName = "is-loading";

interface FilestackImageProps {
  file: File;
  width: number;
  height: number;
  transformOptions?: TransformOptions;
  showLoader?: boolean;
  onMouseEnter?: React.MouseEventHandler;
  onMouseLeave?: React.MouseEventHandler;
  onClick?: (event: React.MouseEvent) => void;
}

const FilestackImageRaw: FunctionComponent<FilestackImageProps> = ({
  file,
  width,
  height,
  transformOptions,
  showLoader = true,
  onMouseEnter,
  onMouseLeave,
  onClick,
}): JSX.Element | null => {
  const imageRef = useRef<HTMLButtonElement | null>(null);
  const { getFileStackUrl } = useFileStackContext();
  const imgSrc = getFileStackUrl(file.handle, transformOptions);

  if (!imgSrc || !width || !height) {
    return null;
  }

  return (
    <button
      type="button"
      ref={imageRef}
      className={`file-stack-image ${showLoader ? loadingClassName : ""}`}
      style={{ width, height }}
      onClick={onClick}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      {!showLoader && (
        <div
          style={{
            width,
            height: width,
            backgroundImage: `url(${imgSrc})`,
          }}
          className="file-stack-image__preview"
          aria-label={file.metadata.filename}
        />
      )}

      {showLoader && imageRef && (
        <FileImageWithLoader
          imageRef={imageRef}
          imgSrc={imgSrc}
          file={file}
          width={width}
          height={height}
        />
      )}
    </button>
  );
};

export const FilestackImage = memo(FilestackImageRaw);

type FileImageWithLoaderProps = {
  imgSrc: string;
  file: File;
  width: number;
  height: number;
  imageRef: RefObject<HTMLElement | null>;
};
const FileImageWithLoaderRaw: FunctionComponent<FileImageWithLoaderProps> = ({
  imgSrc,
  file,
  width,
  height,
  imageRef,
}) => {
  const loadingRequestRef = useRef<() => void>();
  const { getFileStackUrl } = useFileStackContext();
  const fileSrc = getFileStackUrl(file.handle);

  const [fallback, setFallback] = useState(false);

  const handleImageLoaded = useCallback(() => {
    loadingRequestRef.current = fastdom.mutate(() => {
      imageRef.current?.classList.remove(loadingClassName);
    });
  }, [imageRef]);

  const imageOnError = useCallback(() => setFallback(true), []);

  useEffect(
    () => () => {
      if (loadingRequestRef.current) {
        fastdom.clear(loadingRequestRef.current);
      }
    },
    [],
  );

  if (!fallback) {
    return (
      <img
        onLoad={handleImageLoaded}
        onError={imageOnError}
        src={imgSrc}
        width={width}
        height={width}
        alt={file.metadata.filename}
        aria-label={file.metadata.filename}
        loading="lazy"
      />
    );
  }

  return (
    <img src={fileSrc ?? undefined} width={width} height={height} alt={file.metadata.filename} />
  );
};

const FileImageWithLoader = memo(FileImageWithLoaderRaw);
